import React, { useMemo } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

/**
 * Renders a series' episodes with:
 *  - Default "recent first" sort (by project created_at DESC) when the user
 *    has never manually rearranged (series.manual_order !== true).
 *  - Horizontal scroller layout when there are more than 3 episodes.
 *  - Drag-and-drop reordering that persists via `onReorder(newOrder)`.
 *
 * Props:
 *   - series: { id, episodes, manual_order, ... }
 *   - projectsById: Map of project_id -> project doc
 *   - renderCard: (project, episodeNumberForDisplay, seriesId) => JSX
 *   - onReorder: (orderedProjectIds: string[]) => Promise<void>
 */
export const SeriesEpisodesList = ({ series, projectsById, renderCard, onReorder }) => {
  const orderedEpisodes = useMemo(() => {
    const eps = (series.episodes || []).filter((ep) => projectsById.get(ep.project_id));
    if (series.manual_order) {
      // Honor the user's manual order exactly as stored.
      return eps;
    }
    // Default: recent project first (by project created_at DESC).
    return [...eps].sort((a, b) => {
      const pa = projectsById.get(a.project_id);
      const pb = projectsById.get(b.project_id);
      const da = new Date(pa?.created_at || pa?.last_modified || 0).getTime();
      const db = new Date(pb?.created_at || pb?.last_modified || 0).getTime();
      return db - da;
    });
  }, [series.episodes, series.manual_order, projectsById]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedEpisodes.findIndex((ep) => ep.project_id === active.id);
    const newIndex = orderedEpisodes.findIndex((ep) => ep.project_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const nextOrder = arrayMove(orderedEpisodes, oldIndex, newIndex).map((ep) => ep.project_id);
    onReorder(nextOrder);
  };

  if (orderedEpisodes.length === 0) return null;

  const itemIds = orderedEpisodes.map((ep) => ep.project_id);

  // Always use a fixed-width flex row so card size is uniform across every series,
  // regardless of episode count. The container scrolls horizontally ONLY when the
  // cards overflow the available width (i.e. more than ~3 on a typical viewport).
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={itemIds} strategy={horizontalListSortingStrategy}>
        <div
          className="flex gap-4 overflow-x-auto pb-2 snap-x scroll-smooth"
          data-testid={`series-episodes-scroller-${series.id}`}
        >
          {orderedEpisodes.map((ep) => {
            const project = projectsById.get(ep.project_id);
            if (!project) return null;
            return (
              <SortableEpisodeCard
                key={project.id}
                id={project.id}
                wrapperClassName="shrink-0 w-[320px] snap-start"
              >
                {renderCard(project, ep.episode_number, series.id)}
              </SortableEpisodeCard>
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
};

const SortableEpisodeCard = ({ id, children, wrapperClassName = "" }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 50 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} className={`relative group ${wrapperClassName}`} data-testid={`episode-sortable-${id}`}>
      {/* Drag handle — top-left of the card, only visible on hover */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 z-10 inline-flex items-center justify-center w-7 h-7 rounded-md bg-white/90 backdrop-blur text-gray-500 shadow-sm border border-gray-200 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Drag to reorder"
        data-testid={`episode-drag-handle-${id}`}
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      {children}
    </div>
  );
};

export default SeriesEpisodesList;
